import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const ImportCsvPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [listName, setListName] = useState('');
  const [language1, setLanguage1] = useState('');
  const [language2, setLanguage2] = useState('');
  const [swapColumns, setSwapColumns] = useState(false);
  const [error, setError] = useState('');
  const [skippedRows, setSkippedRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [csvFormat, setCsvFormat] = useState('simple'); // 'simple' (2 cols) or 'extended' (6 cols)

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    const result = [];
    let skipped = 0;
    let detectedFormat = 'simple';

    for (const line of lines) {
      // Try different delimiters
      let parts = line.includes(';') ? line.split(';') : line.split(',');

      if (parts.length >= 2) {
        const word1 = parts[0].trim().replace(/^["']|["']$/g, '');
        const word2 = parts[1].trim().replace(/^["']|["']$/g, '');

        if (word1 && word2) {
          const row = { word1, word2 };

          // Check if this is the extended 6-column format
          if (parts.length >= 6) {
            detectedFormat = 'extended';
            row.description1 = parts[2]?.trim().replace(/^["']|["']$/g, '') || '';
            row.description2 = parts[3]?.trim().replace(/^["']|["']$/g, '') || '';
            row.imageUrl = parts[4]?.trim().replace(/^["']|["']$/g, '') || '';
            row.note = parts[5]?.trim().replace(/^["']|["']$/g, '') || '';
          }

          result.push(row);
        } else {
          skipped++;
        }
      } else {
        skipped++;
      }
    }

    return { data: result, skipped, format: detectedFormat };
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');

    try {
      const text = await selectedFile.text();
      const { data, skipped, format } = parseCSV(text);

      if (data.length === 0) {
        setError('No valid data found in CSV file');
        setPreview([]);
        return;
      }

      setPreview(data.slice(0, 10)); // Show first 10 rows
      setSkippedRows(skipped);
      setCsvFormat(format);

      // Set default list name from filename
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, '');
      setListName(fileName);
    } catch (err) {
      setError('Error reading file');
    }
  };

  const handleImport = async () => {
    if (!file || !listName.trim() || !language1.trim() || !language2.trim()) {
      setError(t('errors.required'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const text = await file.text();
      const { data } = parseCSV(text);

      // Swap columns if needed and map all fields
      const vocabularies = data.map(row => {
        const vocab = {
          strWord1: swapColumns ? row.word2 : row.word1,
          strWord2: swapColumns ? row.word1 : row.word2,
        };

        // Include extended fields if present (for 6-column format)
        if (row.description1 !== undefined) {
          vocab.strDescription1 = swapColumns ? row.description2 : row.description1;
          vocab.strDescription2 = swapColumns ? row.description1 : row.description2;
        }
        if (row.imageUrl !== undefined) {
          vocab.strImageUrl = row.imageUrl;
        }
        if (row.note !== undefined) {
          vocab.strNote = row.note;
        }

        return vocab;
      });

      const response = await api.post('/lists/import-csv', {
        strName: listName.trim(),
        strLanguage1Name: language1.trim(),
        strLanguage2Name: language2.trim(),
        vocabularies: vocabularies.slice(0, 200), // Limit to 200
      });

      navigate(`/lists/${response.data.id}`);
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        className="btn btn-outline"
        onClick={() => navigate('/lists')}
        style={{ marginBottom: '1rem' }}
      >
        &larr; {t('common.back')}
      </button>

      <h1 className="page-title">{t('csv.title')}</h1>

      <div className="card" style={{ marginTop: '1rem' }}>
        {error && (
          <div className="form-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* File selection */}
        <div className="form-group">
          <label className="form-label">{t('csv.selectFile')}</label>
          <input
            type="file"
            accept=".csv,.txt"
            onChange={handleFileChange}
            className="form-input"
          />
        </div>

        {/* Preview */}
        {preview.length > 0 && (
          <>
            <div className="form-group">
              <label className="form-label">
                {t('csv.preview')} ({t('csv.rowsFound')}: {preview.length}+
                {skippedRows > 0 && `, ${t('csv.rowsSkipped')}: ${skippedRows}`})
                {csvFormat === 'extended' && (
                  <span style={{ marginLeft: '0.5rem', color: 'var(--color-primary)', fontSize: '0.85rem' }}>
                    - {t('csv.extendedFormat') || 'Extended format (6 columns)'}
                  </span>
                )}
              </label>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: csvFormat === 'extended' ? '0.85rem' : '1rem' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                        {swapColumns ? t('csv.column2') : t('csv.column1')}
                      </th>
                      <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                        {swapColumns ? t('csv.column1') : t('csv.column2')}
                      </th>
                      {csvFormat === 'extended' && (
                        <>
                          <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                            {t('csv.description1') || 'Description 1'}
                          </th>
                          <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                            {t('csv.description2') || 'Description 2'}
                          </th>
                          <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                            {t('csv.imageUrl') || 'Image URL'}
                          </th>
                          <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                            {t('csv.note') || 'Note'}
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i}>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                          {swapColumns ? row.word2 : row.word1}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                          {swapColumns ? row.word1 : row.word2}
                        </td>
                        {csvFormat === 'extended' && (
                          <>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {swapColumns ? row.description2 : row.description1}
                            </td>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {swapColumns ? row.description1 : row.description2}
                            </td>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.imageUrl && (
                                <a href={row.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                                  {row.imageUrl.length > 20 ? '...' + row.imageUrl.slice(-20) : row.imageUrl}
                                </a>
                              )}
                            </td>
                            <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.note}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={swapColumns}
                  onChange={(e) => setSwapColumns(e.target.checked)}
                />
                {t('csv.assignLanguages')} (Swap columns)
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="listName" className="form-label">
                {t('csv.listName')} *
              </label>
              <input
                type="text"
                id="listName"
                className="form-input"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="language1" className="form-label">
                {t('lists.language1')} *
              </label>
              <input
                type="text"
                id="language1"
                className="form-input"
                value={language1}
                onChange={(e) => setLanguage1(e.target.value)}
                placeholder={t('lists.language1Placeholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="language2" className="form-label">
                {t('lists.language2')} *
              </label>
              <input
                type="text"
                id="language2"
                className="form-input"
                value={language2}
                onChange={(e) => setLanguage2(e.target.value)}
                placeholder={t('lists.language2Placeholder')}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('csv.import')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportCsvPage;
